import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Context } from "../../../context/Context";
import { Button, CircleX, History, Input, Loader, NoticeBox, Plus, Send, Sparkles } from "../../ui";
import { useModalLayer } from "../../modals/modalHooks";
import {
  archiveCopilotConversationAPI,
  cancelCopilotPlanAPI,
  confirmCopilotPlanAPI,
  createCopilotConversationAPI,
  editCopilotPlanAPI,
  fetchCopilotConfigAPI,
  fetchCopilotConversationAPI,
  fetchCopilotConversationsAPI,
  regenerateCopilotPlanAPI,
  sendCopilotMessageAPI,
  stageCopilotImageAPI,
  undoCopilotPlanAPI,
} from "../../../utils/api/menuCopilot";
import AiBalanceNotice, { aiWalletNoticeOf } from "../../billing/aiWallet/AiBalanceNotice";
import AiWalletUsageSummary from "../../billing/aiWallet/AiWalletUsageSummary";
import { notifyAiWalletChanged } from "../../billing/aiWallet/aiWalletMenu";
import CopilotMessage from "./CopilotMessage";
import { copilotErrorMessage, copilotText } from "./copilotText";
import { menuErrorMessage } from "../menuRole";
import "./MenuCopilot.css";

// Remembers which conversation was open for a menu (per browser). The
// conversation itself is stored on the server.
const storageKey = (menuId) => `lud-menu-copilot:${menuId}`;
const readStored = (menuId) => {
  try {
    return localStorage.getItem(storageKey(menuId)) || "";
  } catch (error) {
    return "";
  }
};
const writeStored = (menuId, id) => {
  try {
    if (id) localStorage.setItem(storageKey(menuId), id);
    else localStorage.removeItem(storageKey(menuId));
  } catch (error) {
    // storage unavailable: the chat still works for this session
  }
};

// Identifies one submitted message (letters, digits, "-"): the server answers
// a repeated id with the stored reply instead of running the AI again.
const newRequestId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}${Math.random().toString(36).slice(2, 6)}`;

// Menu Copilot side panel for the open menu. Questions are answered from the
// database; changes come back as operation cards and are only written when
// the user presses Apply (executed and audited by the backend).
export default function MenuCopilot({ authorize, menuId, menuName, canWrite, onClose, onApplied }) {
  const { lang } = useContext(Context);
  const t = copilotText(lang);
  const ref = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  // The message being sent: { signature, requestId }. Kept when no answer
  // arrived, so sending the same message again is the same request.
  const pending = useRef(null);
  const [config, setConfig] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [plans, setPlans] = useState({});
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [busyPlan, setBusyPlan] = useState(null);
  const [error, setError] = useState("");
  // The AI Wallet of the restaurant refused the request (balance, frozen, ...).
  const [walletNotice, setWalletNotice] = useState(null);
  // What the last AI operation used from the AI Wallet and what is left (backend).
  const [walletUsage, setWalletUsage] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  useModalLayer(true, { containerRef: ref, onEscape: (event) => !event?.defaultPrevented && onClose(), lockScroll: false });

  const applyConversation = useCallback((next) => {
    setConversation(next);
    setPlans((current) => ({ ...current, ...Object.fromEntries((next.plans || []).map((p) => [p._id, p])) }));
  }, []);
  const updatePlan = (plan) => setPlans((current) => ({ ...current, [plan._id]: plan }));

  useEffect(() => {
    let alive = true;
    setConversation(null);
    setPlans({});
    setError("");
    setWalletNotice(null);
    setWalletUsage(null);
    (async () => {
      try {
        const cfg = await fetchCopilotConfigAPI(menuId, lang);
        if (alive) setConfig(cfg);
      } catch (err) {
        if (alive) setConfig({ enabled: false, canWrite: false, suggestions: [], error: true });
      }
      const stored = readStored(menuId);
      if (!stored) return;
      try {
        const loaded = await fetchCopilotConversationAPI(stored);
        if (alive) applyConversation(loaded);
      } catch (err) {
        writeStored(menuId, "");
      }
    })();
    return () => {
      alive = false;
    };
    // Reload when the open menu changes (and for the language of the suggestions).
  }, [menuId, lang, applyConversation]);

  const messageCount = conversation?.messages?.length || 0;
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messageCount, sending]);

  const refresh = async () => {
    if (!conversation) return;
    try {
      applyConversation(await fetchCopilotConversationAPI(conversation._id));
    } catch (err) {
      // the plan result is already shown; the chat refreshes on the next message
    }
  };

  // A response of an AI operation: show its wallet usage and let the Menu
  // page's balance indicator re-read the wallet.
  const noteUsage = (res) => {
    setWalletUsage(res?.walletUsage || null);
    if (res?.walletUsage) notifyAiWalletChanged();
  };

  const showError = (err) => {
    const notice = aiWalletNoticeOf(err);
    setWalletNotice(notice);
    if (notice) {
      setWalletUsage(null);
      notifyAiWalletChanged();
    }
    setError(notice ? "" : copilotErrorMessage(err, t));
  };

  // Every Copilot call is re-checked for the menu's restaurant right before it is made
  // (NA-010 / NA-011); a refused action sends nothing.
  const allowed = () => {
    if (authorize?.()) return true;
    setError(menuErrorMessage({ status: 403 }, lang));
    return false;
  };

  const send = async (payload) => {
    if (sending) return;
    if (!allowed()) return;
    setSending(true);
    setError("");
    setWalletNotice(null);
    try {
      let current = conversation;
      if (!current) {
        current = await createCopilotConversationAPI(menuId, lang);
        writeStored(menuId, current._id);
        applyConversation(current);
      }
      const signature = `${current._id}:${JSON.stringify(payload)}`;
      const requestId = pending.current?.signature === signature ? pending.current.requestId : newRequestId();
      pending.current = { signature, requestId };
      const res = await sendCopilotMessageAPI(current._id, { ...payload, requestId }, lang);
      pending.current = null;
      applyConversation(res.conversation);
      if (res.plan) updatePlan(res.plan);
      noteUsage(res);
      if (payload.text) setText("");
    } catch (err) {
      // An answer from the server ends the request (except "still being
      // processed"); without one the next send repeats it under the same id.
      if (err.status && err.code !== "REQUEST_IN_PROGRESS") pending.current = null;
      showError(err);
    } finally {
      setSending(false);
      inputRef.current?.focus?.();
    }
  };

  const submit = (event) => {
    event?.preventDefault?.();
    const value = text.trim();
    if (value) send({ text: value });
  };

  const newChat = () => {
    writeStored(menuId, "");
    setConversation(null);
    setPlans({});
    setHistoryOpen(false);
    setError("");
    setWalletNotice(null);
    setWalletUsage(null);
  };

  const openHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (!next) return;
    if (!allowed()) return;
    try {
      setHistory(await fetchCopilotConversationsAPI(menuId));
    } catch (err) {
      setHistory([]);
    }
  };

  const openConversation = async (id) => {
    if (!allowed()) return;
    try {
      applyConversation(await fetchCopilotConversationAPI(id));
      writeStored(menuId, id);
      setHistoryOpen(false);
    } catch (err) {
      setError(t.loadFailed);
    }
  };

  const archive = async (id) => {
    if (!allowed()) return;
    try {
      await archiveCopilotConversationAPI(id);
      setHistory((list) => list.filter((c) => c._id !== id));
      if (conversation?._id === id) newChat();
    } catch (err) {
      setError(copilotErrorMessage(err, t));
    }
  };

  const runPlan = async (plan, action) => {
    if (busyPlan) return false;
    if (!allowed()) return false;
    setBusyPlan(plan._id);
    setError("");
    setWalletNotice(null);
    try {
      await action();
      return true;
    } catch (err) {
      if (err.details?.plan) updatePlan(err.details.plan);
      if (err.code === "PLAN_EXPIRED") updatePlan({ ...plan, status: "expired", expired: true, canRegenerate: true });
      showError(err);
      return false;
    } finally {
      setBusyPlan(null);
    }
  };

  const planActions = (plan) => ({
    onConfirm: ({ verifiedOperations }) =>
      runPlan(plan, async () => {
        const res = await confirmCopilotPlanAPI(plan._id, plan.revision, { verifiedOperations });
        updatePlan(res.plan);
        if (["completed", "partially_completed"].includes(res.plan.status)) onApplied?.();
        await refresh();
      }),
    onCancel: () =>
      runPlan(plan, async () => {
        updatePlan(await cancelCopilotPlanAPI(plan._id));
        await refresh();
      }),
    onEdit: (edits) => runPlan(plan, async () => updatePlan(await editCopilotPlanAPI(plan._id, plan.revision, edits))),
    // A NEW proposal on the current menu; it still needs its own confirmation.
    onRegenerate: () =>
      runPlan(plan, async () => {
        const res = await regenerateCopilotPlanAPI(plan._id, lang);
        noteUsage(res);
        if (res.plan) updatePlan(res.plan);
        if (res.conversation) applyConversation(res.conversation);
        else await refresh();
      }),
    onUploadImage: (index, file) => runPlan(plan, async () => updatePlan(await stageCopilotImageAPI(plan._id, index, plan.revision, file))),
    onUndo: () =>
      runPlan(plan, async () => {
        const res = await undoCopilotPlanAPI(plan._id);
        updatePlan(res.plan);
        onApplied?.();
        await refresh();
      }),
  });

  const messages = conversation?.messages || [];
  const lastIndex = messages.length - 1;
  const disabled = config && !config.enabled;
  const readOnly = config && !config.canWrite;

  return (
    <aside ref={ref} tabIndex={-1} className="menu-copilot" role="dialog" aria-modal="false" aria-label={t.title} dir={lang === "ar" ? "rtl" : "ltr"}>
      <header className="menu-copilot-head">
        <Sparkles size={18} aria-hidden="true" className="menu-copilot-logo" />
        <div className="menu-copilot-heading">
          <h2>{t.title}</h2>
          {menuName && <p dir="auto">{t.subtitle(menuName)}</p>}
        </div>
        <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<History size={16} />} aria-label={t.history} title={t.history} aria-expanded={historyOpen} onClick={openHistory} />
        <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<Plus size={16} />} aria-label={t.newChat} title={t.newChat} onClick={newChat} />
        <Button type="button" variant="warning" appearance="soft" size="sm" rounded icon={<CircleX size={17} />} aria-label={t.close} title={t.close} onClick={onClose} />
      </header>

      {historyOpen && (
        <nav className="menu-copilot-history" aria-label={t.history}>
          {!history.length && <p className="menu-note">{t.noHistory}</p>}
          <ul>
            {history.map((c) => (
              <li key={c._id} className={conversation?._id === c._id ? "is-current" : ""}>
                <Button type="button" variant="primary" appearance="text" size="sm" onClick={() => openConversation(c._id)}>
                  <span dir="auto">{c.title || t.untitled}</span>
                </Button>
                <small>{new Date(c.updatedAt).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn" : "en-US", { dateStyle: "medium", timeStyle: "short" })}</small>
                <Button type="button" variant="danger" appearance="text" size="xs" icon={<CircleX size={13} />} aria-label={t.close} onClick={() => archive(c._id)} />
              </li>
            ))}
          </ul>
        </nav>
      )}

      <ol ref={listRef} className="menu-copilot-messages" aria-live="polite" aria-busy={sending || undefined}>
        {!messages.length && (
          <li className="menu-copilot-empty">
            <p>{t.intro}</p>
            {disabled && <NoticeBox variant="warning" size="sm">{t.disabled}</NoticeBox>}
            {readOnly && !disabled && <NoticeBox variant="info" size="sm">{t.readOnlyNote}</NoticeBox>}
            {!disabled && config?.suggestions?.length > 0 && (
              <div className="menu-copilot-suggestions">
                <span>{t.tryAsking}</span>
                {config.suggestions.map((suggestion) => (
                  <Button key={suggestion} type="button" variant="primary" appearance="outline" size="sm" rounded disabled={sending} onClick={() => send({ text: suggestion })}>
                    <span dir="auto">{suggestion}</span>
                  </Button>
                ))}
              </div>
            )}
          </li>
        )}
        {messages.map((message, i) => (
          <CopilotMessage
            key={message._id || i}
            message={message}
            plan={message.planId ? plans[message.planId] : null}
            t={t}
            lang={lang}
            canWrite={Boolean(canWrite && config?.canWrite)}
            busy={Boolean(busyPlan) || sending}
            isLatestClarify={i === lastIndex}
            onChoose={(clarificationId, slot, entityId) => send({ choice: { clarificationId, slot, entityId } })}
            planActions={planActions}
          />
        ))}
        {sending && (
          <li className="copilot-msg is-assistant">
            <p className="copilot-bubble copilot-thinking">
              <Loader size={14} className="copilot-spin" aria-hidden="true" /> {t.thinking}
            </p>
          </li>
        )}
      </ol>

      {walletUsage && !walletNotice && !sending && <AiWalletUsageSummary usage={walletUsage} lang={lang} className="menu-copilot-usage" />}
      {walletNotice && (
        <div className="menu-copilot-wallet">
          <AiBalanceNotice notice={walletNotice} lang={lang} />
        </div>
      )}
      {error && (
        <p className="menu-error menu-copilot-error" role="alert">
          {error}
        </p>
      )}

      <form className="menu-copilot-input" onSubmit={submit}>
        <Input
          ref={inputRef}
          textarea
          rows={2}
          dir="auto"
          aria-label={t.placeholder}
          placeholder={t.placeholder}
          value={text}
          maxLength={2000}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) submit(e);
          }}
        />
        <Button type="submit" variant="primary" appearance="filled" size="sm" rounded icon={<Send size={16} />} aria-label={t.send} title={t.send} loading={sending} disabled={disabled || !text.trim()} />
      </form>
    </aside>
  );
}
